import { InjectionToken, Type } from '@angular/core';
import { AGUIconName } from '../components/icon/icon-paths';

/**
 * One of the portal's four module colours. Each name resolves to the
 * `--agu-module-*` / `--agu-tint-*` pair the token layer declares, and is what
 * `ra-module-accent()` cascades onto a screen (see _ra-tokens.scss). A closed
 * union rather than a free string, so a typo is a compile error instead of a
 * screen that silently renders in the default blue.
 */
export type AGUAppModuleAccent = 'blue' | 'teal' | 'orange' | 'plum';

/**
 * A sidebar nav item's tool colour: the four module accents plus the three the 10.23 prototype gives Home (sky), Saved (amber) and
 * Settings (slate), which are nav-only and so are not module accents (P/Member_Hub.html:1887-1925). Derived from the accent union so a
 * new accent is a nav tone with no second edit.
 */
export type AGUNavTone = AGUAppModuleAccent | 'sky' | 'amber' | 'slate';

/** What a module's Home dashboard tile says. */
export interface AGUAppModuleHomeCopy {
    /** The tile's sentence, under the module name. */
    readonly Body: string;
    /** Primary action label ('Search grants'). */
    readonly Primary: string;
    /** Secondary action label; `{N}` is substituted with the module's recommendation count. */
    readonly Secondary: string;
    /** Leading 13px glyph on the tile's primary CTA — the mockup's magnifier on the three finders, the upload tray on Career Map (home.html:509-524). */
    readonly PrimaryIcon?: AGUIconName;
}

/**
 * One self-contained module of an AGU app.
 *
 * A module owns a root component, an identity for nav, a default place to land,
 * and its own entitlement requirement. It knows nothing about which app it is
 * mounted in, which is what lets the same module be the WHOLE of a
 * single-module app (Grant Finder) and ONE TAB of a composite app (Research
 * Accelerator) with no code difference — the composition lives in the Dashboard
 * record's UIConfigDetails, not here.
 *
 * Definitions are registered once, globally, on {@link AGU_APP_MODULES}; each
 * surface filters that registry down to its own composition.
 */
export interface AGUAppModuleDefinition {
    /**
     * Stable identifier used in a composition's `modules` array and in
     * AGUAppLocation.ModuleKey. Changing it breaks deployed metadata rows, so it
     * is a durable contract value, not a display string.
     */
    readonly Key: string;
    /** Page title, and the nav label unless {@link NavLabel} overrides it. */
    readonly Title: string;
    /**
     * Sidebar nav text, when the rail wants a shorter word than the page title
     * ('Grants' for 'Grant Finder'). Absent means the rail uses `Title` — see
     * `SidebarNavLabel`.
     */
    readonly NavLabel?: string;
    /** Font Awesome class for nav and dashboard tiles. Free-tier glyphs only —
     *  Pro-only glyphs render blank (ui-best-practices §7). */
    readonly Icon: string;
    /** The portal's own stroke glyph (agu-icon). When set, the sidebar and Home tile render it instead of `Icon`. */
    readonly IconName?: AGUIconName;
    /** One sentence, shown on the member dashboard tile. */
    readonly Description: string;
    /** View the module lands on when it becomes active. */
    readonly DefaultView: string;
    /** The component the surface renders while this module is active. */
    readonly RootComponent: Type<unknown>;
    /**
     * Optional contribution to a composite app's member dashboard — a tile body
     * or recommendation strip for this module. Single-module apps never render
     * it (they have no dashboard module), which is why it is optional rather
     * than a second required component.
     */
    readonly SummaryComponent?: Type<unknown>;
    /**
     * MJ role names that grant this module. EMPTY MEANS OPEN to anyone who holds
     * the Application — that is the deliberate default for member-facing
     * modules, where the Application grant IS the entitlement. Modules that
     * must be narrower list their roles and the surface drops them for users
     * who hold none.
     */
    readonly RequiredRoleNames: readonly string[];
    /**
     * True for modules that are navigable but not listed in the sidebar — a
     * profile page reached from the identity block, a search page reached from
     * the sidebar search box. Undefined means listed.
     */
    readonly HiddenFromNav?: boolean;
    /**
     * Whether the composite app's Home dashboard gives this module a tile.
     * Required, not optional-with-a-default: Home's grid IS this flag, so
     * "someone forgot to say" must be a compile error rather than a module that
     * quietly vanishes from the member's landing screen.
     */
    readonly HomeTile: boolean;
    /**
     * The module's colour throughout the portal — its Home tile, its screen
     * accent, its rail. Required for the same reason `HomeTile` is: a missing
     * accent is invisible (everything falls back to blue) and therefore never
     * gets noticed.
     */
    readonly Accent: AGUAppModuleAccent;
    /** The sidebar rail's tone for this module when it differs from {@link Accent} (Home sky, Saved amber, Settings slate). Absent means `Accent`. */
    readonly NavTone?: AGUNavTone;
    /**
     * Copy for this module's Home tile. Present exactly when `HomeTile` is
     * true — a module with no tile has nowhere to render it.
     */
    readonly HomeCopy?: AGUAppModuleHomeCopy;
}

/**
 * The global module registry. Multi-provider: each feature module contributes
 * its own definition, and every surface resolves its composition against the
 * union. Provided with `multi: true`, so the injected value is an array of
 * arrays flattened by {@link FlattenModuleRegistry}.
 */
export const AGU_APP_MODULES = new InjectionToken<readonly AGUAppModuleDefinition[][]>(
    'AGUAppModules'
);

/**
 * Collapse the multi-provider registry into a flat, key-unique list.
 *
 * Later contributions win on a duplicate key, and the duplicate is reported
 * rather than silently dropped: two modules claiming one key means one of them
 * is unreachable in every composition that names it, which is a wiring bug the
 * developer needs to see.
 */
export function FlattenModuleRegistry(
    contributions: readonly AGUAppModuleDefinition[][] | null
): readonly AGUAppModuleDefinition[] {
    const byKey = new Map<string, AGUAppModuleDefinition>();
    for (const group of contributions ?? []) {
        for (const definition of group) {
            if (byKey.has(definition.Key)) {
                console.warn(
                    `[agu-app-shell] duplicate module key '${definition.Key}' — ` +
                        `'${definition.Title}' replaces '${byKey.get(definition.Key)?.Title}'`
                );
            }
            byKey.set(definition.Key, definition);
        }
    }
    return Array.from(byKey.values());
}
